import { supabase } from '@/lib/supabase';

const VAPID_PUBLIC_KEY_STORAGE = 'worklynx_vapid_public_key';

export type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPushPermissionState(): PushPermissionState {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return registration;
  } catch {
    return null;
  }
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) throw new Error('Notifications not supported');
  return Notification.requestPermission();
}

export async function subscribeToPush(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  try {
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) return existingSubscription;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
    });

    return subscription;
  } catch {
    return null;
  }
}

export async function savePushSubscription(
  userId: string,
  organizationId: string,
  subscription: PushSubscription,
): Promise<void> {
  const subscriptionJson = subscription.toJSON();
  const endpoint = subscription.endpoint;

  const { error: deleteError } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId)
    .eq('endpoint', endpoint);
  if (deleteError && deleteError.code !== 'PGRST116') throw deleteError;

  const { error } = await supabase.from('push_subscriptions').insert({
    user_id: userId,
    organization_id: organizationId,
    endpoint,
    p256dh: subscriptionJson.keys?.p256dh ?? '',
    auth: subscriptionJson.keys?.auth ?? '',
    user_agent: navigator.userAgent,
  });
  if (error) throw error;
}

export async function removePushSubscription(userId: string): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', userId)
        .eq('endpoint', endpoint);
    }
  } catch {
    // Silently ignore cleanup errors
  }
}

export function getStoredVapidKey(): string | null {
  try {
    return localStorage.getItem(VAPID_PUBLIC_KEY_STORAGE);
  } catch {
    return null;
  }
}

export function setStoredVapidKey(key: string): void {
  try {
    localStorage.setItem(VAPID_PUBLIC_KEY_STORAGE, key);
  } catch {
    // Silently ignore storage errors
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
